import { useEffect, useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import GameModal from "./GameModal";
import { MODAL_TYPES } from "./GameModal";
import CaguamaAnimation from "./CaguamaAnimation";
import quotes from "./images/quotes.png";
import duck from "./images/duck-easy.png";
import background from "./images/background.png";
import star from "./images/star.png";
import { useGameContext } from "./GameContext";
import { forbiddenWords } from "../utils/stopWords";
import { useSaveScore } from "../utils/useSaveScore";
import { useAuth } from "./AuthContext";

const API = process.env.REACT_APP_API_URL || "";

const STREAK_CONFIG = {
  easy: { confetti: 5, caguama: 12 },
  medium: { confetti: 4, caguama: 8 },
  hard: { confetti: 3, caguama: 6 },
};

const getStreakRecords = () => {
  try {
    return JSON.parse(localStorage.getItem("kp_streak_records") || "{}");
  } catch {
    return {};
  }
};

const saveStreakRecord = (level, streak) => {
  try {
    const records = getStreakRecords();
    if (streak > (records[level] || 0)) {
      localStorage.setItem(
        "kp_streak_records",
        JSON.stringify({ ...records, [level]: streak })
      );
    }
  } catch {}
};

export const load = async (request, { signal } = {}) => {
  try {
    const url = new URL(request.url, window.location.origin);
    const difficultyParam = url.searchParams.get("difficulty");
    const response = await fetch(request, { signal });
    if (!response.ok) throw new Error("Failed to fetch quote");
    const data = await response.json();
    return {
      quote: data.text,
      difficulty:
        difficultyParam && ["easy", "medium", "hard"].includes(difficultyParam)
          ? difficultyParam
          : "easy",
    };
  } catch (error) {
    console.error("Error in load function:", error);
    return { quote: "", difficulty: "easy", error: error.message };
  }
};

const updatePhraseToGame = (phrase, level) => {
  const words = phrase.split(" ");
  if (words.length === 0) throw new Error("No hay palabras en la frase.");
  const modifiedWords = [...words];
  const missingWords = [];
  const removedIndices = [];
  const isAllowedWord = (word) => {
    const clean = word.replace(/[.,!¡?¿:\"'()\-]/g, "").toLowerCase();
    return clean.length > 3 && !forbiddenWords.includes(clean);
  };
  if (level === "easy") {
    const longWordIndices = words
      .map((word, index) => ({ word, index }))
      .filter(({ word }) => isAllowedWord(word))
      .map(({ index }) => index);
    if (longWordIndices.length === 0)
      throw new Error("No hay palabras válidas para ocultar.");
    const selectedIndex =
      longWordIndices[Math.floor(Math.random() * longWordIndices.length)];
    const word = words[selectedIndex];
    modifiedWords[selectedIndex] = word.replace(/[a-záéíóúüñÁÉÍÓÚÜÑ]/gi, "_");
    missingWords.push(word);
    removedIndices.push(selectedIndex);
    return { phrase: modifiedWords.join(" "), missingWords, removedIndices };
  }
  if (level === "medium") {
    const candidatePairs = [];
    for (let i = 0; i <= words.length - 2; i++) {
      if (isAllowedWord(words[i]) && isAllowedWord(words[i + 1]))
        candidatePairs.push({
          startIndex: i,
          score: words[i].length + words[i + 1].length,
        });
    }
    if (candidatePairs.length === 0)
      for (let i = 0; i <= words.length - 2; i++)
        candidatePairs.push({ startIndex: i, score: 1 });
    candidatePairs.sort((a, b) => b.score - a.score);
    const topScore = candidatePairs[0].score;
    const topCandidates = candidatePairs.filter((p) => p.score === topScore);
    const { startIndex } =
      topCandidates[Math.floor(Math.random() * topCandidates.length)];
    for (let i = 0; i < 2; i++) {
      const word = words[startIndex + i];
      modifiedWords[startIndex + i] = word.replace(
        /[a-záéíóúüñÁÉÍÓÚÜÑ]/gi,
        "_"
      );
      missingWords.push(word);
      removedIndices.push(startIndex + i);
    }
    return { phrase: modifiedWords.join(" "), missingWords, removedIndices };
  }
  if (level === "hard") {
    const wordCount = words.length;
    if (wordCount <= 4) {
      const keywordIndices = words
        .map((word, index) => ({ word, index }))
        .filter(({ word }) => isAllowedWord(word))
        .map(({ index }) => index);
      const protectedIndex = keywordIndices.length
        ? keywordIndices[Math.floor(Math.random() * keywordIndices.length)]
        : Math.floor(Math.random() * words.length);
      const removableIndices = words
        .map((_, index) => index)
        .filter((index) => index !== protectedIndex);
      const selectedIndices = removableIndices
        .sort(() => 0.5 - Math.random())
        .slice(0, Math.min(2, removableIndices.length));
      selectedIndices.forEach((index) => {
        const word = words[index];
        missingWords.push(word);
        modifiedWords[index] = word.replace(/[a-záéíóúüñÁÉÍÓÚÜÑ]/gi, "_");
        removedIndices.push(index);
      });
      return { phrase: modifiedWords.join(" "), missingWords, removedIndices };
    }
    const longAllowedIndices = words
      .map((word, index) => ({ word, index }))
      .filter(({ word }) => isAllowedWord(word))
      .map(({ index }) => index);
    const shuffled = [...longAllowedIndices].sort(() => 0.5 - Math.random());
    const selectedIndices = shuffled.slice(0, Math.min(3, shuffled.length));
    if (selectedIndices.length < 3) {
      const remainingIndices = words
        .map((_, index) => index)
        .filter((index) => !selectedIndices.includes(index));
      selectedIndices.push(
        ...remainingIndices
          .sort(() => 0.5 - Math.random())
          .slice(0, 3 - selectedIndices.length)
      );
    }
    selectedIndices.forEach((index) => {
      const word = words[index];
      missingWords.push(word);
      modifiedWords[index] = word.replace(/[a-záéíóúüñÁÉÍÓÚÜÑ]/gi, "_");
      removedIndices.push(index);
    });
    return { phrase: modifiedWords.join(" "), missingWords, removedIndices };
  }
  throw new Error("Nivel no reconocido.");
};

const addNumbers = (a, b) => Number(a || 0) + Number(b || 0);

const QuotesGame = () => {
  const location = useLocation();
  const [localScore, setLocalScore] = useState(0);
  const {
    setIsPlaying,
    setScore: setContextScore,
    score: contextScore,
  } = useGameContext();
  const { token } = useAuth();
  const [quote, setQuote] = useState("");
  const [completeQuote, setCompleteQuote] = useState("");
  const [userInput, setUserInput] = useState("");
  const [missingWords, setMissingWords] = useState([]);
  const [level, setLevel] = useState("");
  const [oportunidades, setOportunidades] = useState(2);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [startTime, setStartTime] = useState(Date.now());
  const [modalState, setModalState] = useState({
    isOpen: false,
    type: null,
    correctAnswer: "",
    bonusMessage: undefined,
    titleOverride: undefined,
  });
  const lastVisitedRef = useRef(null);
  const requestIdRef = useRef(0);
  const { saveScore, saveRecord, fetchTodayScores } = useSaveScore();
  const [consecutiveCorrect, setConsecutiveCorrect] = useState(0);
  const [highScore, setHighScore] = useState(0);
  const [hoy, setHoy] = useState(0);
  const verifyLockRef = useRef(false);
  const [missedOnce, setMissedOnce] = useState(false);
  const [showCaguama, setShowCaguama] = useState(false);
  const [caguamaStreak, setCaguamaStreak] = useState(0);

  // const karimeDrawing =
  //   "https://res.cloudinary.com/dbvqeiqxb/image/upload/w_110,h_110,c_fill,f_auto,q_auto/v1748135153/kar_drawing_wgyeqi.png";
  const karimeDrawing =
    "https://res.cloudinary.com/dbvqeiqxb/image/upload/v1780206630/ChatGPT_Image_May_31_2026_12_47_21_AM_jq7ncn.png";

  // Función helper para guardar la sesión (agrégala arriba, antes del return)
  const saveGameSession = async (difficulty) => {
    if (!token) return;
    try {
      await fetch(`${API}/api/games/session`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          difficulty,
          game_type: "completa_la_frase",
        }),
      });
    } catch (err) {
      console.error("Error guardando sesión:", err);
    }
  };

  // Cargar récord y hoy desde DB
  useEffect(() => {
    if (!token) return;
    fetch(`${API}/api/auth/profile`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((data) => {
        if (data.record_completa_frase)
          setHighScore(data.record_completa_frase);
      })
      .catch(() => {});

    fetchTodayScores().then((data) => {
      if (data?.frases_hoy) setHoy(Number(data.frases_hoy));
    });
  }, [token]);

  useEffect(() => {
    setContextScore(localScore);
  }, [localScore]);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const difficultyParam = queryParams.get("difficulty");
    const newLevel = ["easy", "medium", "hard"].includes(difficultyParam)
      ? difficultyParam
      : "easy";
    const shouldReset =
      lastVisitedRef.current && lastVisitedRef.current !== newLevel;
    lastVisitedRef.current = newLevel;
    setLevel(newLevel);
    getPhrase(shouldReset);
  }, [location.search]);

  const getPhrase = async (reset = false) => {
    const id = ++requestIdRef.current;
    setStartTime(Date.now());
    setIsLoading(true);
    setError(null);
    setMissedOnce(false);
    try {
      const queryParams = new URLSearchParams(location.search);
      const difficulty = queryParams.get("difficulty") || "easy";
      const url = `/api/quote/random?difficulty=${difficulty}${
        reset ? "&reset=true" : ""
      }`;
      const data = await load(new Request(url));
      if (requestIdRef.current !== id) return;
      if (data.error || !data.quote)
        throw new Error(data.error || "No quote received");
      setCompleteQuote(data.quote);
      setOportunidades(2);
      const gameProps = updatePhraseToGame(data.quote, difficulty);
      setQuote(gameProps.phrase);
      setMissingWords(gameProps.missingWords);
      setUserInput("");
    } catch (error) {
      if (requestIdRef.current === id) setError(error.message);
    } finally {
      if (requestIdRef.current === id) setIsLoading(false);
    }
  };

  const normalizeSpanishText = (text) => {
    let t = (text || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[\u200B-\u200D\uFEFF]/g, "");
    try {
      t = t.replace(/[^\p{L}\p{N}\s]/gu, "");
    } catch {
      t = t.replace(/[^A-Za-z0-9áéíóúüñÁÉÍÓÚÜÑ\s]/g, "");
    }
    return t.replace(/\s+/g, " ").toLowerCase().trim();
  };

  const extractMissingSubphrase = (quote, missingWords) => {
    const quoteWords = quote.split(/\s+/);
    const normalized = (str) =>
      str
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase();
    const normalizedMissingWords = missingWords.map(normalized);
    const normalizedQuoteWords = quoteWords.map(normalized);
    const missingIndexes = normalizedQuoteWords
      .map((word, idx) => (normalizedMissingWords.includes(word) ? idx : null))
      .filter((idx) => idx !== null);
    if (missingIndexes.length === 0) return "";
    const start = Math.min(...missingIndexes);
    const end = Math.max(...missingIndexes);
    return quoteWords.slice(start, end + 1).join(" ");
  };

  const checkAnswer = () => {
    if (verifyLockRef.current) return;
    verifyLockRef.current = true;
    setTimeout(() => (verifyLockRef.current = false), 500);
    if (!userInput || userInput.trim() === "") return;
    const normalizedUserInput = normalizeSpanishText(userInput);
    const normalizedQuote = normalizeSpanishText(completeQuote);
    const normalizedMissingWords = missingWords.map(normalizeSpanishText);
    const inputWords = normalizedUserInput.split(" ");
    const isFullSentenceCorrect = normalizedUserInput === normalizedQuote;
    const missingSubphrase = extractMissingSubphrase(
      completeQuote,
      missingWords
    );
    const isMissingSubphraseCorrect =
      normalizedUserInput === normalizeSpanishText(missingSubphrase);
    let allWordsCorrect = false;
    if (level === "hard") {
      allWordsCorrect =
        inputWords.length === normalizedMissingWords.length &&
        normalizedMissingWords.every((word) => inputWords.includes(word));
    } else {
      allWordsCorrect =
        inputWords.length === normalizedMissingWords.length &&
        inputWords.every((word, i) => word === normalizedMissingWords[i]);
    }

    if (isFullSentenceCorrect || allWordsCorrect || isMissingSubphraseCorrect) {
      const timeTaken = (Date.now() - startTime) / 1000;
      let points = 1;
      const newBonusMessage = [];
      const allowFullSentenceBonus = !missedOnce;
      const allowSpeedBonus =
        !missedOnce && (level === "medium" || level === "hard");
      if (allowFullSentenceBonus && isFullSentenceCorrect) {
        points += 1;
        newBonusMessage.push("💅 +1 por frase completa");
      }
      let timeThreshold = null;
      if (level === "medium") timeThreshold = 4;
      if (level === "hard") timeThreshold = 6;
      if (
        allowSpeedBonus &&
        timeThreshold !== null &&
        timeTaken < timeThreshold
      ) {
        points += 1;
        newBonusMessage.push("⏱️ +1 por rapidez");
      }

      setLocalScore((prev) => {
        const newScore = addNumbers(prev, points);
        saveScore("completa_frase", points);
        setHoy((h) => h + points);
        if (newScore > highScore) {
          setHighScore(newScore);
          saveRecord("frases", newScore);
        }
        return newScore;
      });

      const newStreak = missedOnce ? 0 : consecutiveCorrect + 1;
      setConsecutiveCorrect(newStreak);
      saveStreakRecord(level, newStreak);

      const levelConfig = STREAK_CONFIG[level] || STREAK_CONFIG.easy;
      const isCaguamaMilestone =
        !missedOnce && newStreak > 0 && newStreak % levelConfig.caguama === 0;
      const isConfettiMilestone =
        !missedOnce && newStreak > 0 && newStreak % levelConfig.confetti === 0;

      if (!isCaguamaMilestone && isConfettiMilestone)
        newBonusMessage.push(`🔥 ¡${newStreak} seguidas, bb!`);

      if (isCaguamaMilestone) {
        setCaguamaStreak(newStreak);
        setShowCaguama(true);
        saveGameSession(level);
      } else {
        setModalState({
          isOpen: true,
          type: MODAL_TYPES.CORRECT,
          correctAnswer: completeQuote,
          bonusMessage: newBonusMessage,
          titleOverride: undefined,
          streak: newStreak,
          showConfetti: isConfettiMilestone,
        });
      }
    } else {
      const newOportunidades = oportunidades - 1;
      setOportunidades(newOportunidades);
      setMissedOnce(true);
      setConsecutiveCorrect(0);
      setModalState({
        isOpen: true,
        type:
          newOportunidades > 0 ? MODAL_TYPES.INCORRECT : MODAL_TYPES.GAME_OVER,
        correctAnswer: completeQuote,
        titleOverride: "¡U\u200Cps!",
        bonusMessage: undefined,
        streak: 0,
      });
    }
  };

  const closeModal = () => setModalState((s) => ({ ...s, isOpen: false }));

  useEffect(() => {
    setIsPlaying(true);
    return () => setIsPlaying(false);
  }, []);

  const scoreToShow =
    typeof contextScore === "number" ? contextScore : localScore;

  useEffect(() => {
    setContextScore(localScore);
  }, [localScore, setContextScore]);

  return (
    <div
      className="p-5 flex flex-col h-full font-quicksand bg-cover bg-center bg-no-repeat"
      style={{ backgroundImage: `url(${background})` }}
    >
      <div
        className="absolute top-[90px] right-4 flex items-center gap-2 text-sm font-semibold"
        style={{
          background: "rgba(255,255,255,0.85)",
          border: "1.5px solid #f9a8d4",
          borderRadius: "999px",
          padding: "5px 14px",
          backdropFilter: "blur(8px)",
          color: "#b06ab3",
          boxShadow: "0 2px 8px rgba(176,106,179,0.12)",
        }}
      >
        <img src={duck} alt="Duck" className="w-5 h-5" />
        <span>Hoy: {hoy}</span>
        <span style={{ color: "#d1d5db" }}>|</span>
        <img src={star} alt="Star" className="w-5 h-5" />
        <span>Récord: {highScore}</span>
        {consecutiveCorrect > 0 && (
          <>
            <span style={{ color: "#d1d5db" }}>|</span>
            <span>🔥 {consecutiveCorrect}</span>
          </>
        )}
      </div>

      <div className="flex-1 flex flex-col pt-24">
        <div className="flex flex-col items-center gap-2 mb-10">
          <div
            className="font-extrabold text-2xl text-center leading-tight"
            style={{ color: "#2d1f3d" }}
          >
            <span>Karime Pindter</span>
            <span className="block">una vez dijo:</span>
          </div>
        </div>

        <div
          className="rounded-3xl shadow-md py-8 pb-8 pt-20 px-9 w-full max-w-md text-center relative"
          style={{
            background: "rgba(255,255,255,0.88)",
            border: "2px solid #7dd3fc",
            backdropFilter: "blur(8px)",
            boxShadow: "0 4px 24px rgba(125,211,252,0.2)",
          }}
        >
          <div className="w-20 h-20 absolute -top-8 left-5">
            <img
              src={karimeDrawing}
              className="w-full h-full object-cover rounded-full"
              style={{ border: "2px solid #7dd3fc" }}
              alt="Karime"
            />
          </div>

          {isLoading ? (
            <div
              className="flex items-center justify-center gap-2"
              style={{ color: "#9b7eaa" }}
            >
              <div
                className="animate-spin rounded-full h-5 w-5 border-2"
                style={{
                  borderColor: "#b06ab3",
                  borderTopColor: "transparent",
                }}
              />
              <span className="text-sm">Cargando...</span>
            </div>
          ) : error ? (
            <div className="text-center">
              <p className="text-red-400 text-sm mb-3">{error}</p>
              <button
                onClick={() => getPhrase(true)}
                className="text-sm font-bold px-4 py-2 rounded-2xl text-white"
                style={{ background: "#7dd3fc" }}
              >
                Reintentar
              </button>
            </div>
          ) : (
            <>
              <div className="flex mb-4">
                <img
                  src={quotes}
                  alt="Quotes"
                  className="w-7 h-7 -mt-2 mr-2 flex-shrink-0"
                />
                <p
                  className="font-bold uppercase text-sm text-left leading-relaxed"
                  style={{ color: "#4a5568" }}
                >
                  {quote}
                </p>
              </div>
              <input
                className="w-full px-4 py-3 rounded-2xl text-sm font-medium focus:outline-none"
                style={{
                  border: "1.5px solid #7dd3fc",
                  background: "rgba(255,255,255,0.9)",
                  color: "#2d1f3d",
                }}
                value={userInput}
                onChange={(e) => setUserInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && checkAnswer()}
                disabled={oportunidades === 0}
                placeholder="Completa la frase aquí"
              />
              <button
                onClick={checkAnswer}
                disabled={oportunidades === 0 || isLoading}
                className="mt-3 w-full py-3 rounded-2xl font-bold text-white text-sm active:scale-95 transition-transform"
                style={{
                  background:
                    oportunidades === 0
                      ? "#e2e8f0"
                      : "linear-gradient(135deg, #7dd3fc, #38bdf8)",
                  color: oportunidades === 0 ? "#9ca3af" : "white",
                  boxShadow:
                    oportunidades === 0
                      ? "none"
                      : "0 4px 12px rgba(125,211,252,0.4)",
                }}
              >
                Verificar
              </button>
            </>
          )}
        </div>
      </div>

      {modalState.isOpen && (
        <GameModal
          isOpen={modalState.isOpen}
          type={modalState.type}
          correctAnswer={modalState.correctAnswer}
          bonusMessage={modalState.bonusMessage}
          titleOverride={modalState.titleOverride}
          onClose={closeModal}
          onNextQuestion={() => {
            closeModal();
            getPhrase();
          }}
          onRetry={() => {
            closeModal();
            setUserInput("");
          }}
          showConfetti={!!modalState.showConfetti}
        />
      )}

      {showCaguama && (
        <CaguamaAnimation
          streak={caguamaStreak}
          difficulty={level}
          onClose={() => {
            setShowCaguama(false);
            getPhrase();
          }}
        />
      )}
    </div>
  );
};

export default QuotesGame;
